import { create } from 'zustand';
import type { AlertDto, AssetDto, GeofenceDto, TrailDto } from './api';

type Mode = 'LIVE' | 'REPLAY';
type LayerToggles = {
  geofences: boolean;
  heatmap: boolean;
  satellite: boolean;
};

type ReplayState = {
  date: string; // YYYY-MM-DD in tenant local time
  cursorSec: number; // 0–86399
  speed: number; // 1, 2, 5, 10, 60, 300
  playing: boolean;
};

type CommandCenterState = {
  // Connection
  connected: boolean;
  setConnected: (v: boolean) => void;

  // Assets
  assets: Record<string, AssetDto>;
  setAssets: (assets: AssetDto[]) => void;
  upsertAsset: (patch: Partial<AssetDto> & { id: string }) => void;
  applyFix: (fix: {
    asset_id: string;
    lat: number;
    lng: number;
    heading?: number | null;
    speed_kmh?: number | null;
    recorded_at: string;
    status?: AssetDto['status'];
  }) => void;

  // Selection
  selectedAssetId: string | null;
  select: (id: string | null) => void;

  // Trail
  trail: TrailDto | null;
  setTrail: (t: TrailDto | null) => void;

  // Alerts
  alerts: AlertDto[];
  setAlerts: (a: AlertDto[]) => void;
  upsertAlert: (a: AlertDto) => void;

  // Geofences
  geofences: GeofenceDto[];
  setGeofences: (g: GeofenceDto[]) => void;

  // Filters & search
  search: string;
  setSearch: (s: string) => void;
  statusFilter: Set<AssetDto['status']>;
  toggleStatusFilter: (s: AssetDto['status']) => void;

  // Mode + time machine
  mode: Mode;
  setMode: (m: Mode) => void;
  replay: ReplayState;
  setReplay: (patch: Partial<ReplayState>) => void;

  // Layers
  layers: LayerToggles;
  toggleLayer: (k: keyof LayerToggles) => void;

  // Right rail mode
  rightPanel: 'alerts' | 'detail';
  setRightPanel: (p: 'alerts' | 'detail') => void;

  // Command palette
  paletteOpen: boolean;
  setPaletteOpen: (v: boolean) => void;
};

export const useCommandCenter = create<CommandCenterState>((set) => ({
  connected: false,
  setConnected: (v) => set({ connected: v }),

  assets: {},
  setAssets: (assets) =>
    set({
      assets: Object.fromEntries(assets.map((a) => [a.id, a])),
    }),
  upsertAsset: (patch) =>
    set((s) => ({
      assets: { ...s.assets, [patch.id]: { ...s.assets[patch.id], ...patch } as AssetDto },
    })),
  applyFix: (fix) =>
    set((s) => {
      const prev = s.assets[fix.asset_id];
      if (!prev) return s;
      return {
        assets: {
          ...s.assets,
          [fix.asset_id]: {
            ...prev,
            lat: fix.lat,
            lng: fix.lng,
            heading: fix.heading ?? prev.heading,
            speed_kmh: fix.speed_kmh ?? prev.speed_kmh,
            last_fix_at: fix.recorded_at,
            status: fix.status ?? prev.status,
          },
        },
      };
    }),

  selectedAssetId: null,
  select: (id) => set({ selectedAssetId: id, rightPanel: id ? 'detail' : 'alerts' }),

  trail: null,
  setTrail: (t) => set({ trail: t }),

  alerts: [],
  setAlerts: (a) => set({ alerts: a }),
  upsertAlert: (a) =>
    set((s) => {
      const idx = s.alerts.findIndex((x) => x.id === a.id);
      if (idx === -1) return { alerts: [a, ...s.alerts].slice(0, 500) };
      const next = [...s.alerts];
      next[idx] = { ...next[idx], ...a };
      return { alerts: next };
    }),

  geofences: [],
  setGeofences: (g) => set({ geofences: g }),

  search: '',
  setSearch: (s) => set({ search: s }),
  statusFilter: new Set(),
  toggleStatusFilter: (s) =>
    set((state) => {
      const next = new Set(state.statusFilter);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return { statusFilter: next };
    }),

  mode: 'LIVE',
  setMode: (m) => set({ mode: m }),
  replay: {
    date: new Date().toISOString().slice(0, 10),
    cursorSec: 0,
    speed: 5,
    playing: false,
  },
  setReplay: (patch) => set((s) => ({ replay: { ...s.replay, ...patch } })),

  layers: { geofences: true, heatmap: false, satellite: false },
  toggleLayer: (k) => set((s) => ({ layers: { ...s.layers, [k]: !s.layers[k] } })),

  rightPanel: 'alerts',
  setRightPanel: (p) => set({ rightPanel: p }),

  paletteOpen: false,
  setPaletteOpen: (v) => set({ paletteOpen: v }),
}));

export const selectFilteredAssets = (state: CommandCenterState): AssetDto[] => {
  const arr = Object.values(state.assets);
  const q = state.search.trim().toLowerCase();
  return arr
    .filter((a) => (state.statusFilter.size === 0 ? true : state.statusFilter.has(a.status)))
    .filter((a) =>
      q.length === 0
        ? true
        : a.name.toLowerCase().includes(q) ||
          a.code.toLowerCase().includes(q) ||
          (a.plate_number?.toLowerCase().includes(q) ?? false),
    )
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const selectKpis = (state: CommandCenterState) => {
  const list = Object.values(state.assets);
  const active = list.filter((a) => a.status === 'ACTIVE').length;
  const idle = list.filter((a) => a.status === 'IDLE').length;
  const offline = list.filter((a) => a.status === 'OFFLINE' || a.status === 'STALE').length;
  const alerted = list.filter((a) => a.status === 'ALERT').length;
  const openAlerts = state.alerts.filter((a) => a.status !== 'RESOLVED').length;
  return { active, idle, offline, alerted, openAlerts, total: list.length };
};
